import { useState } from "react";
import axios from "axios";
import { API, formatPrice } from "../lib/constants";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const inputCls =
  "mt-1.5 w-full rounded-lg border border-espresso/20 bg-ivory px-3.5 py-2.5 text-sm text-espresso outline-none transition-colors focus:border-champagne";
const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-espresso/60";

export const OrderForm = ({ perfume }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", quantity: 1, message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/orders`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        perfume_id: perfume.id || "",
        perfume_name: perfume.name,
        collection: (perfume.collections || [])[0] || "",
        size: perfume.size || "30 ml",
        price: Number(perfume.price) || 0,
        quantity: Number(form.quantity) || 1,
        message: form.message,
      });
      setSent(true);
      toast.success("Votre demande est bien enregistrée");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Une erreur est survenue. Réessayez.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-espresso/10 bg-ivory/60 p-5" data-testid="order-form">
      <p className="eyebrow text-champagne">Commande express</p>
      {sent ? (
        <div className="mt-4 flex items-start gap-3" data-testid="order-success-message">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-champagne/20 text-champagne">
            <Check size={13} />
          </span>
          <p className="text-sm leading-relaxed text-espresso/75">
            Merci {form.name.trim().split(" ")[0]} ! Votre demande pour <strong>{form.quantity} × {perfume.name}</strong> est
            enregistrée — Carine vous recontacte très vite pour finaliser la livraison.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
          <label className={`${labelCls} col-span-2`}>
            Nom complet
            <input data-testid="order-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Votre nom" />
          </label>
          <label className={`${labelCls} col-span-2`}>
            Téléphone (optionnel)
            <input data-testid="order-phone-input" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="06 …" />
          </label>
          <label className={`${labelCls} col-span-2`}>
            Email (optionnel — pour recevoir la confirmation)
            <input data-testid="order-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="vous@exemple.fr" />
          </label>
          <label className={labelCls}>
            Quantité
            <input data-testid="order-quantity-input" type="number" min="1" max="20" required value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} />
          </label>
          <div className="flex items-end pb-1.5">
            <p className="text-sm text-espresso/70">
              Total : <span className="font-serif text-lg text-espresso">{formatPrice((Number(perfume.price) || 0) * (Number(form.quantity) || 1))}</span>{" "}
              <span className="text-[9px] uppercase tracking-[0.15em] text-taupe">hors livraison</span>
            </p>
          </div>
          <label className={`${labelCls} col-span-2`}>
            Message (optionnel)
            <textarea data-testid="order-message-input" rows={2} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} placeholder="Une demande particulière ?" />
          </label>
          <button
            data-testid="order-submit-button"
            disabled={busy}
            className="btn-pill col-span-2 border border-espresso/30 py-3.5 text-espresso hover:bg-espresso hover:text-ivory disabled:opacity-60"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : null}
            {busy ? "Envoi…" : "Envoyer ma demande de commande"}
          </button>
        </form>
      )}
    </div>
  );
};
